import { Component, OnInit, ViewChild } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { NgForm } from '@angular/forms';

import {
  ActivityFormatService, AgeGroupService, ArrayService, AuthService, ChurchService,
  MediaPerformanceService, DatabaseService, EmailService, ServiceYearMemberService, FestivalUserService   
} from '../../../core/services';

import { 
  ActivityFormat, MediaPerformance, Church, DocumentReference, ServiceYearMember, NameValue, FestivalUser 
} from '../../../core/models';

import { PageClass } from '../../../core/classes/page';
import { Activity, MediaStatus } from 'src/core/enums';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-add-media-performance',
  templateUrl: './add-performance.component.html',
  styleUrls: ['./add-performance.component.less', '../../../core/styles/common.less']
})

export class AddMediaPerformanceComponent extends PageClass implements OnInit {

  performance: any = {
    isActive : true,
    isDeleted : false,
    isMemberFlagUpdated: false
  }

  filterChurchList: DocumentReference[] = [];

  activity: NameValue;
  formatList: ActivityFormat[];
  performanceCategoryList: NameValue[] = [];
  locationList: NameValue[] = [];
  approverList: DocumentReference[] = [];
  statusList: NameValue[] = [];

  serviceYearMembers: DocumentReference[] = [];
  AllServiceYearMembers: ServiceYearMember[] = [];
  memberListPlaceHolder: string = 'Required';

  performanceCategoryObj: NameValue = null;
  formatObj: ActivityFormat = null;

  formatPerformanceList: MediaPerformance[] = [];

  isFullAccessUser: boolean = false;
  isLoading: boolean = true;
  areMembersAccepted: boolean = true;
  isApprovalConfirmed: boolean = false;
  isConsentMessageDisabled: boolean = false;

  submitLabel: string = 'Save';
  
  @ViewChild('performanceForm', null) performanceForm: NgForm;

  constructor(
    protected activityFormatService: ActivityFormatService,
    protected ageGroupService: AgeGroupService,
    protected arrayService: ArrayService,
    protected authService: AuthService,
    protected mediaPerformanceService: MediaPerformanceService,
    protected churchService: ChurchService,
    protected databaseService: DatabaseService,
    protected emailService: EmailService,
    protected serviceYearMemberService: ServiceYearMemberService,
    protected festivalUserService: FestivalUserService,

    protected alertController: AlertController,
    protected modalController: ModalController
  ) {
    super(ageGroupService, arrayService, authService, churchService, databaseService, festivalUserService);
  }

  protected loadData = async () => {

    if (this.currentUser.isActive && !this.currentUser.isDeleted) {
      this.isFullAccessUser = this.currentUser.isAdmin || this.currentUser.isActivityLeader;

      this.activity = this.activityFormatService.getActivityList().find(a => a.value === Activity.Media);
      this.performanceCategoryList = this.mediaPerformanceService.getPerformanceCategoryList();
      this.locationList = this.mediaPerformanceService.getLocationList();
      this.statusList = this.mediaPerformanceService.getPerformanceStatusList();

      if (this.isFullAccessUser)
        this.filterChurchList = this.churchList;
      else {
        this.filterChurchList.push(this.currentUser.church);
        this.performance.church = this.currentUser.church;
        this.onChurchSelected();
      }

      this.isLoading = false;
    } else {
      this.dismissModal();
    }
  }

  public onChurchSelected = async () => {
    
    this.approverList = [];

    (await this.festivalUserService.getActivityApprovers(this.performance.church))
      .subscribe(approverList => {
      approverList
        .filter((a: FestivalUser) => a.activityList.findIndex(a => a.value === 'Media') > -1)
        .forEach((approver: FestivalUser) => {
          this.approverList.push(
              {
                  reference: this.databaseService.doc(`${this.festivalUserService.collection}/${approver.id}`).ref,
                  displayName: approver.user.displayName
              }
          );
      });

      if (this.approverList?.length > 0) 
        this.performance.approver = this.approverList[0];
      else 
        this.performance.approver = null;

      this.setSubmitButtonLabel();
    });

    await this.churchService.getChurchDocument(this.performance.church.reference.id)
      .then((church: Church) => {
        if (church && church.city && church.city === 'Melbourne')
          this.performance.location = this.locationList[0];
        else
          this.performance.location = this.locationList[1];
    });

    if (!!this.performance.format) {
      this.getMemberGroupList();
      this.getFormatPerformanceList();
    }
  }

  public onPerformanceCategorySelected = (): void => {
      
    this.clearData();

    this.performance.category = this.performanceCategoryObj;

    this.activityFormatService.getFormatList(this.activity, this.performance.category)
        .subscribe(formatList => {
          this.formatList = formatList;

          if (this.formatList?.length == 1)
            this.performance.format = this.getFormatFromObj(formatList[0]);
        });
  }

  public onApproverSelected = (): void => {
    this.setSubmitButtonLabel();
  }

  private getFormatFromObj = (format: ActivityFormat): DocumentReference => {
    return { 
      displayName: `${format.displayName}`, 
      reference: this.databaseService.doc(`${this.activityFormatService.collection}/${format.id}`).ref
    }
  }

  public onFormatSelected = () => {
    this.performance.format = this.getFormatFromObj(this.formatObj);

    this.setSubmitButtonLabel();

    this.memberListPlaceHolder = this.formatObj.areMembersRequiredOnCreation ? 'Required' : '';
    this.isConsentMessageDisabled = this.isPastDate(this.formatObj.creationDueDate);
    
    if (new Date(this.formatObj.creationStartDate) > this.today && !this.currentUser.isAdmin)
      this.showErrorMessage('Cannot Create Performance',
        `Sorry you cannot create ${this.performance.category.name} performance until 
        ${new Date(this.formatObj.creationStartDate).toDateString()}`, !this.currentUser.isAdmin
      );

    if (this.isPastDate(this.formatObj.creationDueDate) && !this.currentUser.isAdmin)
        this.showErrorMessage('Cannot Create Performance',
          `Sorry you cannot create ${this.performance.category.name} performance after 
          ${new Date(this.formatObj.creationDueDate).toDateString()}`, !this.currentUser.isAdmin
        );

      this.getMemberGroupList();

      this.getFormatPerformanceList();
  }

  showErrorMessage = async (header: string, errorMessage: string, clearData: boolean) => {
    const alert = await this.alertController.create({
      header: header,
      message: errorMessage,
      buttons: ['OK'],
    });

    alert.onDidDismiss().then(() => { 
      if (clearData)
        this.clearData();
    });

    await alert.present();
  }

  private clearData = (): void => {    
    this.formatObj = null;
    this.AllServiceYearMembers = [];
    this.serviceYearMembers = [];
    this.performance.format = null;
    this.performance.memberList = [];
  }

  private getMemberGroupList() {

    if (!this.performance.church || 
        !this.performance.category || 
        !this.performance.format) {
      return;
    }

    this.serviceYearMembers = [];
    this.performance.memberList = [];

    this.serviceYearMemberService.getTeamMembersYearLevels(
      this.performance.church, 
      this.formatObj.yearLevelList.length == this.ageLevelList.length ? null : this.formatObj.yearLevelList)
      .subscribe(serviceYearMembers => {

        this.AllServiceYearMembers = serviceYearMembers;
        this.serviceYearMembers = [];
        serviceYearMembers.map(a => this.serviceYearMembers.push({
          displayName: `${a.member.displayName} - ${a.yearLevel.displayName}`, 
          reference: this.databaseService.doc(`serviceYearMembers/${a.id}`).ref
        }));

        this.serviceYearMembers.sort(this.arrayService.orderByDisplayName);
      });
  }

  public onMemberListUpdated = async (): Promise<void> => {
    this.areMembersAccepted = false;
    
    this.mediaPerformanceService.getChurchCategoryPerformanceList(this.performance.church, this.performance.category).pipe(take(1)).subscribe((performances: MediaPerformance[]) => {
        if (performances?.length > 0) {
          
          for (var performance of performances) { 
            var members = performance.memberList;

            for (var pMember of this.performance.memberList) {

              if (members.find(m => m.reference.id == pMember.reference.id)) {
                this.showErrorMessage(
                  'Cannot Add Member', 
                  `Sorry, you cannot add member ${pMember.displayName}, member is already in another performance`, false);
                return;
              }
            };         
          };
        }

        this.areMembersAccepted = true;
      })
  }

  public validateAndCreatePerformance = async (): Promise<void> => {
    if (this.performanceForm.invalid) {
      return;
    }

    const membersCount = this.performance.memberList.length;
    if (this.formatObj.areMembersRequiredOnCreation && this.formatObj.hasMinMembersLimit && membersCount < this.formatObj.minMembers) {
      this.showErrorMessage(
        'Cannot Create Performance', 
        `Sorry, you cannot add this performance, # Members cannot be less than ${this.formatObj.minMembers}`, false)
      return;
    }

    if (this.formatObj.hasMaxMembersLimit && membersCount > this.formatObj.maxMembers) {
      this.showErrorMessage(
        'Cannot Create Performance', 
        `Sorry, you cannot add this performance, # Members cannot be more than ${this.formatObj.maxMembers}`, false)
      return;
    }

    if (this.formatObj.hasMaxTeamsLimit && this.formatPerformanceList.length >= this.formatObj.maxTeams) {
      this.showErrorMessage(
        'Cannot Create Performance', 
        `Sorry, you cannot add new ${this.performanceCategoryObj.name} performance for ${this.performance.format.displayName} age 
        since your church already has ${this.formatObj.maxTeams} performance(s)`, false)
      return;
    }

    if (this.formatObj.hasApproval && this.performance.approver.reference.id == this.currentUser.id && !this.performance.isApprovalConfirmed) {
      this.showErrorMessage(
        'Cannot Create Performance', 
        `Sorry, you must confirm the church priest approval for this Media submisstion`, false)
      return;
    }

    await this.addMediaPerformance();
  }

  private addMediaPerformance = async () => {
    
    var newStatus: MediaStatus = this.mediaPerformanceService.calculatePerformanceStatus(this.performance, this.formatObj);
    
    //this.setCompletedUserActions();

    this.performance.status = this.statusList.find(s => s.value == this.mediaPerformanceService.getStatusValue(newStatus));

    await this.mediaPerformanceService.createPerformance(this.performance, this.currentUser);

    await this.emailService.triggerMediaEmail(this.performance, newStatus, null);
    
    alert('Media Performance Successfully Created');

    this.dismissModal();
  }

  private getFormatPerformanceList = (): void => {

    if (this.performance.church && this.performanceCategoryObj && this.formatObj) {
      this.mediaPerformanceService.getChurchFormatPerformanceList(this.performance.church, this.performance.format)
        .subscribe(formatPerformanceList => this.formatPerformanceList = formatPerformanceList);
    }
  }

  private setSubmitButtonLabel = (): void => {
      this.submitLabel = !this.formatObj?.hasApproval ? "Save" : this.performance.isApprovalConfirmed
        ? "Save & Approve" : "Save & Request Approval";
  }

  dismissModal() {
    this.modalController.dismiss();
  }
}
